import React from 'react';
import { Users, Award, Clock, Globe, Target, Heart, Lightbulb, Shield } from 'lucide-react';

const About = () => {
    const stats = [
        { icon: Users, value: '1357+', label: 'Active Clients' },
        { icon: Award, value: '53+', label: 'Products & Services' },
        { icon: Clock, value: '734+', label: 'Installations' },
        { icon: Globe, value: '42652+', label: 'User' }
    ];

    const values = [
        {
            icon: Target,
            title: 'Innovation',
            description: 'We stay ahead of technology trends to deliver cutting-edge solutions that give our clients a competitive advantage.'
        },
        {
            icon: Heart,
            title: 'Client Success',
            description: 'Your success is our success. We go above and beyond to ensure our solutions drive real business value for our clients.'
        },
        {
            icon: Lightbulb,
            title: 'Expertise',
            description: 'Our team combines deep technical knowledge with extensive experience in the finance industry.'
        },
        {
            icon: Shield,
            title: 'Security',
            description: 'We understand the critical importance of security in finance and build it into every solution from the ground up.'
        }
    ];

    const team = [
        {
            name: 'Sarah Johnson',
            role: 'CEO & Founder',
            bio: 'Former finance executive with 15+ years in fintech innovation.',
            image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop'
        },
        {
            name: 'Michael Chen',
            role: 'CTO',
            bio: 'Tech visionary with expertise in scalable financial systems.',
            image: 'https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop'
        },
        {
            name: 'Emily Rodriguez',
            role: 'Head of Design',
            bio: 'UX specialist focused on creating intuitive financial interfaces.',
            image: 'https://images.pexels.com/photos/1036623/pexels-photo-1036623.jpeg?auto=compress&cs=tinysrgb&w=300&h=300&fit=crop'
        }
    ];

    return (
        <section id="about" className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-gray-900 mb-4">About Newline Infotech Pvt Ltd.</h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        We're a dedicated team of technology experts passionate about transforming the finance industry
                        through innovative software solutions.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
                    <div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h3>
                        <div className="space-y-4 text-gray-600 leading-relaxed">
                            <p>
                                Newline Infotech, based in Udaipur, started in 2010 to offer affordable, quality software for small and mid-sized businesses. Today, it serves 150+ clients across sectors, delivering reliable solutions with a focus on evolving technology and global standards.
                            </p>
                            <p>
                                NIPL has grown from humble beginnings into a nationally recognized company known for innovation, practical solutions, and proactive service. Initially focused on providing low-cost, effective solutions for the banking sector, NIPL now delivers advanced services across data centers, finance, and embedded systems. With a track record of successful implementations and satisfied clients, NIPL continues to expand its expertise beyond banking into various industrial verticals.
                            </p>
                            <p>
                                NIPL develops and implements complex web applications. That’s because we deliver both high performance custom web applications and turnkey solutions for maximum business efficiency.
                            </p>
                            <p>
                                We help our clients to choose the right mix of software and hardware infrastructure to fulfill not only their current requirement but also has scalability and performance.                            </p>
                        </div>
                    </div>

                    <div className="relative">
                        <img
                            src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
                            alt="Team collaboration"
                            className="rounded-2xl shadow-lg"
                        />
                        <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white p-6 rounded-xl shadow-lg">
                            <div className="text-2xl font-bold">500+</div>
                            <div className="text-blue-100">Successful Projects</div>
                        </div>
                    </div>
                </div>

                <div className="grid md:grid-cols-4 gap-8 mb-16">
                    {stats.map((stat, index) => (
                        <div key={index} className="text-center bg-white rounded-xl p-6 shadow-lg">
                            <div className="bg-gradient-to-r from-blue-100 to-teal-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                                <stat.icon className="w-6 h-6 text-blue-600" />
                            </div>
                            <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                            <div className="text-gray-600">{stat.label}</div>
                        </div>
                    ))}
                </div>

                <div className="mb-16">
                    <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Our Values</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <div key={index} className="text-center">
                                <div className="bg-gradient-to-r from-blue-100 to-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <value.icon className="w-8 h-8 text-blue-600" />
                                </div>
                                <h4 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h4>
                                <p className="text-gray-600 leading-relaxed">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* <div>
                    <h3 className="text-3xl font-bold text-gray-900 text-center mb-12">Meet Our Leadership</h3>
                    <div className="grid md:grid-cols-3 gap-8">
                        {team.map((member, index) => (
                            <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center hover:shadow-xl transition-shadow">
                                <img
                                    src={member.image}
                                    alt={member.name}
                                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                                />
                                <h4 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h4>
                                <div className="text-blue-600 font-medium mb-3">{member.role}</div>
                                <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
                            </div>
                        ))}
                    </div>
                </div> */}

                <div className="mt-16 bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-8 text-center text-white">
                    <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Finance Office?</h3>
                    <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                        Join hundreds of finance offices that have already transformed their operations with our solutions.
                        Let's discuss how we can help you achieve your goals.
                    </p>
                    <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                        Start Your Journey Today
                    </button>
                </div>
            </div>
        </section>
    );
};

export default About;